import { useRef } from 'react';
import { Link } from '@tanstack/react-router';
import { CopyId, Field, Input, RadioGroup } from '@bb/ui';
import type { CanvasSettings, CanvasView } from '../../../api/client.ts';
import { ScheduledDeliveryForm } from '../../campaigns/schedule/ScheduledDeliveryForm.tsx';
import { TriggerList } from '../../campaigns/trigger/TriggerEditor.tsx';
import { fromLocalInput, issuesBelow, MAX_ENTRY_TRIGGERS, messagesAt, toLocalInput, withEntryType, type ActionEntry, type Entry, type EntryType, type SettingsIssue } from './settings.ts';

export interface WizardStepProps {
  environmentId: string;
  canvas: CanvasView;
  settings: CanvasSettings;
  onChange: (next: CanvasSettings) => void;
  issues: SettingsIssue[];
  readOnly: boolean;
}

const ENTRY_TYPES: Array<{ value: EntryType; label: string }> = [
  { value: 'scheduled', label: 'Scheduled: the audience enters at a set time or on a schedule' },
  { value: 'action_based', label: 'Action-based: users enter when they do something' },
  { value: 'api_triggered', label: 'API-triggered: your backend enters users through the API' },
];

function Errors({ messages }: { messages: string[] }) {
  return (
    <>
      {messages.map((message) => (
        <p key={message} className="bb-field__error" role="alert">
          {message}
        </p>
      ))}
    </>
  );
}

/** How a backend enters users into an API-triggered Canvas (BB-124): the Canvas API ID and a request to copy. */
export function ApiEntryHint({ environmentId, canvas }: { environmentId: string; canvas: CanvasView }) {
  const origin = typeof window === 'undefined' ? '' : window.location.origin;
  const curl = [
    `curl -s ${origin}/server/v1/canvases/${canvas.apiId}/trigger \\`,
    `  -H "Authorization: Bearer $BB_SERVER_KEY" -H "Idempotency-Key: <unique request id>" \\`,
    `  -H 'Content-Type: application/json' -d '{"recipients":[{"externalId":"7731","context":{"symbol":"EURUSD"}}]}'`,
  ].join('\n');
  return (
    <section aria-label="Canvas Trigger API" className="canvas-wizard__api">
      <CopyId label="Canvas API ID" value={canvas.apiId} />
      <p className="hint">
        Your backend calls <code>POST /server/v1/canvases/:apiId/trigger</code> with a server key that has the <code>trigger.canvas</code> scope (
        <Link to="/env/$environmentId/settings/api" params={{ environmentId }}>
          APIs and Identifiers
        </Link>
        ). Each request needs its own <code>Idempotency-Key</code>: a retry with the same key enters no one twice. Recipients&apos; <code>context</code> becomes{' '}
        <code>{'{{context.name}}'}</code> in the messages. Only an active Canvas accepts entries.
      </p>
      <pre aria-label="Example request" className="canvas-wizard__code">
        {curl}
      </pre>
    </section>
  );
}

function EntryWindow({ entry, issues, readOnly, onChange }: { entry: ActionEntry; issues: SettingsIssue[]; readOnly: boolean; onChange: (next: ActionEntry) => void }) {
  const set = (key: 'startAt' | 'endAt', value: string) => {
    const { [key]: _old, ...rest } = entry.window;
    const iso = fromLocalInput(value);
    onChange({ ...entry, window: iso ? { ...rest, [key]: iso } : rest });
  };
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    <fieldset className="audience-group">
      <legend>Entry window</legend>
      <p className="hint">Optional. Actions before the start or after the end do not enter anyone. Times are in your browser&apos;s time zone ({zone}).</p>
      <div className="audience-filter">
        <Field label="Users enter from">
          <Input type="datetime-local" value={toLocalInput(entry.window.startAt)} disabled={readOnly} onChange={(e) => set('startAt', e.target.value)} />
        </Field>
        <Field label="Until" error={messagesAt(issues, 'entry', 'window', 'endAt')[0]}>
          <Input type="datetime-local" value={toLocalInput(entry.window.endAt)} disabled={readOnly} onChange={(e) => set('endAt', e.target.value)} />
        </Field>
      </div>
    </fieldset>
  );
}

/**
 * Entry of a Canvas (BB-111): scheduled (the campaign schedule of BB-99), action-based (any of up to ten triggers of
 * BB-100, within an optional window) or through the Canvas Trigger API (BB-124). Switching back to a type brings back
 * what was entered for it in this session.
 */
export function EntryStep({ environmentId, canvas, settings, onChange, issues, readOnly }: WizardStepProps) {
  const remembered = useRef<Partial<Record<EntryType, Entry>>>({});
  const { entry } = settings;
  const setEntry = (next: Entry) => onChange({ ...settings, entry: next });
  return (
    <section aria-label="Entry" className="canvas-wizard__panel">
      <h2>Entry</h2>
      <RadioGroup
        label="How users enter"
        options={ENTRY_TYPES.map((t) => ({ ...t, disabled: readOnly }))}
        value={entry?.type ?? ''}
        onValueChange={(type) => {
          if (entry) remembered.current[entry.type] = entry;
          onChange(withEntryType(settings, type as EntryType, remembered.current[type as EntryType]));
        }}
      />
      <Errors messages={messagesAt(issues, 'entry')} />
      {entry?.type === 'scheduled' && (
        <ScheduledDeliveryForm
          environmentId={environmentId}
          value={entry.schedule}
          readOnly={readOnly}
          objectLabel="Canvas"
          readOnlyHint=""
          onChange={(schedule) => setEntry({ ...entry, schedule })}
        />
      )}
      {entry?.type === 'scheduled' && <p className="hint">At each run the audience is taken as it is then; with local time, users enter in waves by their time zone.</p>}
      {entry?.type === 'action_based' && (
        <>
          <fieldset className="audience-group">
            <legend>Users enter when they do any of these</legend>
            <TriggerList
              environmentId={environmentId}
              readOnly={readOnly}
              issues={issuesBelow(issues, 'entry')}
              list="triggers"
              items={entry.triggers}
              onChange={(triggers) => setEntry({ ...entry, triggers })}
              itemLabel="Trigger"
              addLabel="Add a trigger"
              min={1}
              max={MAX_ENTRY_TRIGGERS}
            />
            <p className="hint">The same event never enters a user twice. Properties of the event become the entry context, so messages can use them.</p>
          </fieldset>
          <EntryWindow entry={entry} issues={issues} readOnly={readOnly} onChange={setEntry} />
        </>
      )}
      {entry?.type === 'api_triggered' && <ApiEntryHint environmentId={environmentId} canvas={canvas} />}
    </section>
  );
}
